import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LogOut, Globe, Handshake, Cake, Rocket } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useT, LANGS, setStoredLang } from '../lib/i18n';
import api from '../lib/api';

export default function AppHeader({ subtitle }) {
  const { user, logout, refresh } = useAuth();
  const { t, lang } = useT();
  const navigate = useNavigate();

  const handleLogout = () => { logout(); navigate('/'); };

  const changeLang = async (newLang) => {
    if (newLang === lang) return;
    setStoredLang(newLang);
    if (user) {
      try { await api.patch('/user/preferences', { language: newLang }); await refresh(); }
      catch { /* silent */ }
    } else { window.location.reload(); }
  };

  return (
    <header className="bg-navy text-white px-4 md:px-8 py-4">
      <div className="max-w-3xl mx-auto flex items-center justify-between">
        <Link to="/dashboard" className="flex items-center gap-2" data-testid="header-home-link">
          <div className="w-9 h-9 rounded-xl bg-gold flex items-center justify-center text-navy font-heading text-lg font-bold">O</div>
          <div>
            <div className="font-heading text-lg font-bold leading-none">OuiStuPaStu</div>
            {subtitle && <div className="text-[11px] text-white/60 mt-0.5">{subtitle}</div>}
          </div>
        </Link>
        <div className="flex items-center gap-2">
          <div className="relative group" data-testid="language-switcher">
            <button className="h-10 px-3 rounded-full bg-white/10 hover:bg-white/20 flex items-center gap-1.5 text-sm font-heading font-bold transition-colors duration-200" aria-label={t('header.language')}>
              <Globe className="w-4 h-4" />
              <span className="uppercase">{lang}</span>
            </button>
            <div className="absolute right-0 top-full mt-2 min-w-[140px] bg-white text-navy rounded-2xl shadow-squishy border-2 border-cream p-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-200 z-30">
              {LANGS.map((l) => (
                <button key={l.code} onClick={() => changeLang(l.code)}
                  className={`w-full text-left px-3 py-2 rounded-xl font-semibold text-sm hover:bg-cream ${l.code === lang ? 'bg-teal/10 text-teal' : ''}`}
                  data-testid={`lang-option-${l.code}`}>{l.native}</button>
              ))}
            </div>
          </div>
          {user && <span className="hidden sm:block text-sm font-semibold" data-testid="header-user-name">{user.name}</span>}
          <Link to="/super"
            className="h-10 px-3 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-heading font-bold text-xs inline-flex items-center gap-1.5 transition-colors"
            data-testid="header-super" title="Super App">
            <Rocket className="w-4 h-4" /> Super App
          </Link>
          <Link to="/birthdays"
            className="h-10 px-3 rounded-full bg-cream hover:bg-gold/30 text-navy font-heading font-bold text-xs inline-flex items-center gap-1.5 transition-colors"
            data-testid="header-birthdays" title="Birthdays & Gifts">
            <Cake className="w-4 h-4" /> Birthdays
          </Link>
          <Link to="/demo"
            className="h-10 px-3 rounded-full bg-yellow-400/90 hover:bg-yellow-400 text-navy font-heading font-bold text-xs inline-flex items-center gap-1.5 transition-colors"
            data-testid="header-mtn-demo" title="MTN partnership demo">
            <Handshake className="w-4 h-4" /> MTN Demo
          </Link>
          <button onClick={handleLogout}
            className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors duration-200"
            data-testid="header-logout-button" aria-label={t('cta.logout')}>
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </header>
  );
}
